/* eslint @typescript-eslint/no-explicit-any: "off" */
// TODO: Remove the above once "groupData" is properly typed.
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { TreeNodeData, VisibleField } from "@tenforce/product-fields"
import {
    getAllConceptFields,
    getAvailableSortingFields,
    getCollectionItems,
    getConceptGroups,
    getVisibleFieldsList,
    updateVisibleFieldsList,
} from "../../apis/collection"
import { addErrorNotification } from "../notifications/actions"
import {
    FetchConceptFieldsParams,
    ICollectionState,
    IMultiCollectionState,
    FetchCollectionParams,
    ConceptFieldTree,
} from "."
import { GlobalState } from "../../store"
import { fetchFormattingSettings } from "../common/userFormattingSettings/slice"
import { IUserFormattingSettings } from "../applicationheader/components/usermenu"

const getCollectionState = (
    state: IMultiCollectionState,
    collectionId: string,
): ICollectionState => {
    if (!state[collectionId]) {
        state[collectionId] = { ...initialSingleCollectionState }
    }
    return state[collectionId]
}
const defaultSelectorData = {
    id: "-1",
    totalChildrenCount: 0,
    selectedChildrenCount: 0,
    name: "",
    children: [],
}

export const initialSingleCollectionState: ICollectionState = {
    isQueryProcessing: false,
    visibleFields: [],
    visibleFieldsIds: [],
    selectedSorting: [],
    availableSortingOptions: defaultSelectorData,
    selectableFieldsData: {
        allConceptFields: [],
        totalValueCount: 0,
        currentSkip: 0,
        hasMore: false,
        currentFilter: "",
    },
    items: [],
    remainingCount: 0,
}

export const collectionSlice = createSlice({
    name: "collections",

    initialState: {} as IMultiCollectionState,

    reducers: {
        clearCollectionData: (state, action: PayloadAction<string>) => {
            const collectionId = action.payload
            if (state[collectionId]) {
                delete state[collectionId]
            }
        },
        resetCollectionState: (state, action: PayloadAction<string>) => {
            const collectionId = action.payload
            state[collectionId] = { ...initialSingleCollectionState }
        },
    },

    extraReducers: (builder) => {
        builder
            .addCase(fetchVisibleFields.pending, (state, action) => {
                getCollectionState(
                    state,
                    action.meta.arg.fieldId,
                ).isQueryProcessing = true
            })
            .addCase(fetchVisibleFields.fulfilled, (state, action) => {
                const collectionId = action.meta.arg.fieldId
                const collectionState = getCollectionState(state, collectionId)
                collectionState.visibleFields = action.payload?.fields || []
                collectionState.visibleFieldsIds =
                    action.payload?.fields.map(
                        (field: VisibleField) => field.id,
                    ) || []
                collectionState.selectedSorting =
                    action?.payload?.selectedSorting || []
                collectionState.isQueryProcessing = false
            })
            .addCase(fetchVisibleFields.rejected, (state, action) => {
                getCollectionState(
                    state,
                    action.meta.arg.fieldId,
                ).isQueryProcessing = false
            })

            .addCase(updateVisibleFields.pending, (state, action) => {
                getCollectionState(
                    state,
                    action.meta.arg.fieldId,
                ).isQueryProcessing = true
            })
            .addCase(updateVisibleFields.fulfilled, (state, action) => {
                const collectionId = action.meta.arg.fieldId
                const collectionState = getCollectionState(state, collectionId)
                collectionState.visibleFields = action.payload?.fields || []
                collectionState.visibleFieldsIds =
                    action.payload?.fields.map((field) => field.id) || []
                collectionState.selectedSorting =
                    action?.payload?.selectedSorting || []
                collectionState.isQueryProcessing = false
            })
            .addCase(updateVisibleFields.rejected, (state, action) => {
                getCollectionState(
                    state,
                    action.meta.arg.fieldId,
                ).isQueryProcessing = false
            })

            .addCase(fetchConceptFields.pending, (state, action) => {
                getCollectionState(
                    state,
                    action.meta.arg.fieldId,
                ).isQueryProcessing = true
            })
            .addCase(fetchConceptFields.fulfilled, (state, action) => {
                const collectionId = action.meta.arg.fieldId
                const currentFilter = action.meta.arg.filter
                const collectionState = getCollectionState(state, collectionId)
                const fields = action.payload?.fields || []
                const selectedIdSet = new Set(collectionState.visibleFieldsIds)
                fields.forEach((groupItem) => {
                    if (groupItem.hasChildren) {
                        groupItem.children?.forEach((child) => {
                            if (selectedIdSet.has(child.id)) {
                                child.isSelected = true
                            }
                        })
                    } else if (selectedIdSet.has(groupItem.id)) {
                        groupItem.isSelected = true
                    }
                })
                collectionState.isQueryProcessing = false
                if (
                    currentFilter !==
                        collectionState.selectableFieldsData.currentFilter ||
                    action.meta.arg.skip === 0
                ) {
                    collectionState.selectableFieldsData.allConceptFields =
                        fields
                    collectionState.selectableFieldsData.currentSkip =
                        action.payload?.recordsCount || 0
                    collectionState.selectableFieldsData.currentFilter =
                        currentFilter
                } else {
                    collectionState.selectableFieldsData.allConceptFields =
                        mergeFields(
                            collectionState.selectableFieldsData
                                .allConceptFields,
                            fields,
                        )
                    collectionState.selectableFieldsData.currentSkip +=
                        action.payload?.recordsCount || 0
                }
                collectionState.selectableFieldsData.totalValueCount =
                    action.payload?.count || 0
                collectionState.selectableFieldsData.hasMore =
                    collectionState.selectableFieldsData.currentSkip <
                    collectionState.selectableFieldsData.totalValueCount
            })
            .addCase(fetchConceptFields.rejected, (state, action) => {
                getCollectionState(
                    state,
                    action.meta.arg.fieldId,
                ).isQueryProcessing = false
            })
            .addCase(fetchCollectionItems.pending, (state, action) => {
                getCollectionState(
                    state,
                    action.meta.arg.fieldId,
                ).isQueryProcessing = true
            })
            .addCase(fetchCollectionItems.fulfilled, (state, action) => {
                const collectionId = action.meta.arg.fieldId
                const collectionState = getCollectionState(state, collectionId)
                const isFreshCollectionLoad =
                    (action.meta.arg.offset ?? 0) === 0
                collectionState.isQueryProcessing = false
                if (action.payload) {
                    collectionState.items = collectionState.items =
                        isFreshCollectionLoad
                            ? action.payload.items
                            : collectionState.items.concat(
                                  action.payload.items || [],
                              )
                    collectionState.remainingCount =
                        action.payload.count - collectionState.items.length
                }
            })
            .addCase(fetchCollectionItems.rejected, (state, action) => {
                getCollectionState(
                    state,
                    action.meta.arg.fieldId,
                ).isQueryProcessing = false
            })
            .addCase(fetchAvailableSortingFields.fulfilled, (state, action) => {
                if (!action.payload) {
                    return
                }
                getCollectionState(
                    state,
                    action.meta.arg.fieldId,
                ).availableSortingOptions = action.payload
            })
    },
})

export default collectionSlice.reducer

export const { clearCollectionData, resetCollectionState } =
    collectionSlice.actions

export const fetchVisibleFields = createAsyncThunk(
    "collections/fetchVisibleFields",
    async (data: FetchCollectionParams, { dispatch }) => {
        try {
            return await getVisibleFieldsList(
                data.itemId,
                data.fieldId,
                data.odataType,
            )
        } catch (error) {
            dispatch(addErrorNotification(error.message))
        }
    },
)

export const updateVisibleFields = createAsyncThunk(
    "collections/updateVisibleFields",
    async (data: FetchCollectionParams, { dispatch }) => {
        try {
            return await updateVisibleFieldsList(
                data.itemId,
                data.fieldId,
                data.odataType,
                data.selectedFields,
                data.sorting || [],
            )
        } catch (error) {
            dispatch(addErrorNotification(error.message))
        }
    },
)

export const fetchCollectionItems = createAsyncThunk(
    "collections/fetchCollectionItems",
    async (data: FetchCollectionParams, { dispatch, getState }) => {
        const state = getState() as GlobalState

        let userFormattingSettings = state.userFormattingSettings

        if (!userFormattingSettings.isInitialized) {
            const response = await dispatch(fetchFormattingSettings())
            userFormattingSettings = response.payload as IUserFormattingSettings
        }

        try {
            return await getCollectionItems(
                data.itemId,
                data.fieldId,
                data.odataType,
                userFormattingSettings,
                data.offset,
                data.limit,
            )
        } catch (error) {
            dispatch(addErrorNotification(error.message))
        }
    },
)

export const fetchAvailableSortingFields = createAsyncThunk(
    "collections/fetchAvailableSortingFields",
    async (data: FetchCollectionParams, { dispatch }) => {
        try {
            return await getAvailableSortingFields(
                data.itemId,
                data.fieldId,
                data.odataType,
            )
        } catch (error) {
            dispatch(addErrorNotification(error.message))
        }
    },
)

export const fetchConceptGroups = createAsyncThunk(
    "collections/fetchConceptGroups",
    async (_, { dispatch }) => {
        try {
            return await getConceptGroups()
        } catch (error) {
            dispatch(addErrorNotification(error.message))
        }
    },
)

export const fetchConceptFields = createAsyncThunk(
    "collections/fetchAllConceptField",
    async (data: FetchConceptFieldsParams, { dispatch }) => {
        try {
            const groupAction = await dispatch(fetchConceptGroups())
            const groupData = (groupAction as any)?.payload ?? []
            const selectedData = await getAllConceptFields(
                data.filter,
                data.skip,
                data.top,
                groupData,
            )
            return selectedData
        } catch (error) {
            dispatch(addErrorNotification(error.message))
        }
    },
)

const mergeFields = (
    existingFields: TreeNodeData<ConceptFieldTree>[],
    newFields: TreeNodeData<ConceptFieldTree>[],
): TreeNodeData<ConceptFieldTree>[] => {
    const mergedFields = [...existingFields]

    newFields.forEach((newField) => {
        const existingFieldIndex = mergedFields.findIndex(
            (field) => field.id === newField.id,
        )

        if (existingFieldIndex !== -1) {
            const existingField = mergedFields[existingFieldIndex]
            mergedFields[existingFieldIndex] = {
                ...existingField,
                children: [
                    ...(existingField.children || []),
                    ...(newField.children || []),
                ],
            }
        } else {
            mergedFields.push(newField)
        }
    })

    return mergedFields
}
